<template>
  <div class="platform-detail-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>

    <b-row>
      <b-col md="12">
        <KTCodePreview>
          <template v-slot:preview>
            <div
              v-if="error && !connection"
              class="alert alert-danger"
              role="alert"
            >
              {{ error }}
              <div class="mt-3">
                <b-button variant="primary" @click="loadConnectionDetail">
                  <i class="flaticon2-refresh"></i> Thử lại
                </b-button>
                <b-button variant="secondary" class="ml-2" @click="backToList">
                  Quay lại danh sách
                </b-button>
              </div>
            </div>

            <div v-if="loading && !connection" class="text-center py-5">
              <b-spinner variant="primary"></b-spinner>
              <p class="text-muted mt-3">Đang tải thông tin kết nối...</p>
            </div>

            <div v-if="connection" class="platform-detail-page__content">
              <div class="platform-detail-page__header mb-4">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <img
                      :src="getPlatformLogo(connection.platform)"
                      :alt="getPlatformName(connection.platform)"
                      class="platform-logo mr-3"
                    />
                    <div>
                      <h4 class="mb-1">{{ connection.shopName }}</h4>
                      <p class="text-muted mb-0">
                        {{ getPlatformName(connection.platform) }} - ID:
                        {{ connection.shopId }}
                      </p>
                    </div>
                  </div>
                  <div>
                    <b-badge
                      :variant="getStatusVariant(connection.status)"
                      class="px-3 py-2"
                    >
                      {{ getStatusIcon(connection.status) }}
                      {{ getStatusText(connection.status) }}
                    </b-badge>
                  </div>
                </div>
              </div>

              <b-row>
                <b-col lg="8">
                  <b-card class="mb-4">
                    <h5 class="mb-4">Thông tin kết nối</h5>

                    <b-row>
                      <b-col md="6" class="mb-3">
                        <div class="info-item">
                          <label class="text-muted mb-1"
                            >Sàn thương mại điện tử</label
                          >
                          <div>
                            <strong>{{
                              getPlatformName(connection.platform)
                            }}</strong>
                          </div>
                        </div>
                      </b-col>

                      <b-col md="6" class="mb-3">
                        <div class="info-item">
                          <label class="text-muted mb-1">Cửa hàng</label>
                          <div
                            v-if="connection.storeName"
                            class="d-flex align-items-center"
                          >
                            <strong class="mr-2">{{
                              connection.storeName
                            }}</strong>
                            <b-button
                              variant="link"
                              size="sm"
                              class="p-0"
                              @click="showChangeStoreModal"
                            >
                              <i class="flaticon2-edit"></i> Thay đổi
                            </b-button>
                          </div>
                          <div v-else class="text-muted">Chưa liên kết</div>
                        </div>
                      </b-col>

                      <b-col md="6" class="mb-3">
                        <div class="info-item">
                          <label class="text-muted mb-1"
                            >Tài khoản trả góp</label
                          >
                          <div
                            v-if="installmentDisplay.mode === 'ok'"
                            class="d-flex align-items-center flex-wrap"
                          >
                            <strong class="mr-2">{{
                              installmentDisplay.label
                            }}</strong>
                            <b-button
                              variant="link"
                              size="sm"
                              class="p-0"
                              @click="showInstallmentAccountModal = true"
                            >
                              <i class="flaticon2-edit"></i> Thay đổi
                            </b-button>
                          </div>
                          <div
                            v-else-if="installmentDisplay.mode === 'invalid'"
                            class="d-flex flex-column align-items-start"
                          >
                            <div
                              class="d-flex align-items-center flex-wrap mb-1"
                            >
                              <b-badge variant="warning" class="mr-2"
                                >TK không còn hợp lệ</b-badge
                              >
                              <span class="text-muted small"
                                >ID đã lưu:
                                {{ installmentDisplay.staleId }}</span
                              >
                            </div>
                            <b-button
                              variant="link"
                              size="sm"
                              class="p-0"
                              @click="showInstallmentAccountModal = true"
                            >
                              <i class="flaticon2-refresh"></i> Chọn lại tài
                              khoản
                            </b-button>
                          </div>
                          <div
                            v-else
                            class="d-flex align-items-center flex-wrap"
                          >
                            <span class="text-muted mr-2">Chưa cấu hình</span>
                            <b-button
                              variant="link"
                              size="sm"
                              class="p-0"
                              @click="showInstallmentAccountModal = true"
                            >
                              <i class="flaticon2-plus"></i> Thiết lập
                            </b-button>
                          </div>
                        </div>
                      </b-col>

                      <b-col md="6" class="mb-3">
                        <div class="info-item">
                          <label class="text-muted mb-1">Ngày kết nối</label>
                          <div>{{ formatDate(connection.createdAt) }}</div>
                        </div>
                      </b-col>

                      <b-col md="6" class="mb-3">
                        <div class="info-item">
                          <label class="text-muted mb-1">Token hết hạn</label>
                          <div>
                            {{ formatDate(connection.tokenExpiresAt) }}
                            <b-badge
                              v-if="
                                isTokenExpiringSoon(connection.tokenExpiresAt)
                              "
                              variant="warning"
                              class="ml-2"
                            >
                              Sắp hết hạn
                            </b-badge>
                          </div>
                        </div>
                      </b-col>

                      <b-col
                        v-if="
                          connection.status === 'error' &&
                            connection.errorMessage
                        "
                        md="12"
                        class="mb-3"
                      >
                        <div class="info-item">
                          <label class="text-muted mb-1">Thông báo lỗi</label>
                          <div class="alert alert-danger mb-0">
                            {{ connection.errorMessage }}
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card>

                  <b-card
                    v-if="statistics && connection.status === 'connected'"
                    class="mb-4"
                  >
                    <h5 class="mb-4">Thống kê gian hàng</h5>
                    <b-row>
                      <b-col md="4" sm="6" class="mb-3">
                        <div class="stat-item">
                          <div class="stat-item__icon">
                            <i class="flaticon2-box text-primary"></i>
                          </div>
                          <div class="stat-item__content">
                            <div class="stat-item__value">
                              {{ formatNumber(statistics.productCount) }}
                            </div>
                            <div class="stat-item__label text-muted">
                              Sản phẩm
                            </div>
                          </div>
                        </div>
                      </b-col>

                      <b-col md="4" sm="6" class="mb-3">
                        <div class="stat-item">
                          <div class="stat-item__icon">
                            <i class="flaticon2-shopping-cart text-warning"></i>
                          </div>
                          <div class="stat-item__content">
                            <div class="stat-item__value">
                              {{ formatNumber(statistics.pendingOrders) }}
                            </div>
                            <div class="stat-item__label text-muted">
                              Đơn hàng chờ xử lý
                            </div>
                          </div>
                        </div>
                      </b-col>

                      <b-col md="4" sm="6" class="mb-3">
                        <div class="stat-item">
                          <div class="stat-item__icon">
                            <i class="flaticon2-check-mark text-success"></i>
                          </div>
                          <div class="stat-item__content">
                            <div class="stat-item__value">
                              {{ formatNumber(statistics.completedOrders) }}
                            </div>
                            <div class="stat-item__label text-muted">
                              Đơn đã hoàn thành
                            </div>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card>

                  <b-alert
                    v-if="connection.status === 'error'"
                    show
                    variant="warning"
                  >
                    <h6 class="alert-heading">
                      <i class="flaticon2-warning"></i> Kết nối gặp vấn đề
                    </h6>
                    <p class="mb-0">
                      Không thể lấy thống kê từ
                      {{ getPlatformName(connection.platform) }}. Vui lòng làm
                      mới kết nối hoặc kiểm tra lại cấu hình.
                    </p>
                  </b-alert>
                </b-col>

                <b-col lg="4">
                  <b-card class="platform-detail-page__actions">
                    <h5 class="mb-4">Hành động</h5>

                    <div class="action-buttons">
                      <b-button
                        variant="primary"
                        block
                        @click="refreshConnection"
                        :disabled="isRefreshing"
                        class="mb-2"
                      >
                        <b-spinner
                          v-if="isRefreshing"
                          small
                          class="mr-2"
                        ></b-spinner>
                        <i v-else class="flaticon2-refresh mr-2"></i>
                        {{
                          isRefreshing ? 'Đang làm mới...' : 'Làm mới kết nối'
                        }}
                      </b-button>

                      <b-button
                        variant="outline-primary"
                        block
                        @click="showChangeStoreModal"
                        class="mb-2"
                      >
                        <i class="flaticon2-edit mr-2"></i>
                        Thay đổi cửa hàng
                      </b-button>

                      <b-button
                        variant="outline-primary"
                        block
                        @click="showInstallmentAccountModal = true"
                        class="mb-2"
                      >
                        <i class="flaticon2-safe mr-2"></i>
                        Tài khoản trả góp
                      </b-button>

                      <b-button
                        variant="outline-danger"
                        block
                        @click="confirmDisconnect"
                      >
                        <i class="flaticon2-delete mr-2"></i>
                        Ngắt kết nối
                      </b-button>
                    </div>
                  </b-card>

                  <b-card class="mt-4">
                    <h6 class="mb-3">Lưu ý</h6>
                    <ul class="notes-list">
                      <li>Token sẽ tự động được làm mới trước khi hết hạn</li>
                      <li>Thống kê được cập nhật mỗi 30 phút</li>
                      <li>Ngắt kết nối sẽ xóa toàn bộ dữ liệu liên kết</li>
                    </ul>
                  </b-card>
                </b-col>
              </b-row>
            </div>

            <StoreAssociationModal
              v-if="connection"
              v-model="showStoreAssociationModal"
              :shop-name="connection.shopName"
              :config-id="connection.id"
              :stores="availableStores"
              @saved="handleStoreSaved"
            />

            <InstallmentAccountModal
              v-if="connection"
              v-model="showInstallmentAccountModal"
              :shop-name="connection.shopName"
              :config-id="connection.id"
              :initial-installment-account-id="connection.installmentAccountId"
              @saved="handleInstallmentAccountSaved"
            />

            <b-modal
              id="disconnect-modal"
              v-model="showDisconnectModal"
              title="Xác nhận ngắt kết nối"
              @ok="handleDisconnect"
              ok-title="Ngắt kết nối"
              ok-variant="danger"
              cancel-title="Hủy"
            >
              <p v-if="connection">
                Bạn có chắc chắn muốn ngắt kết nối với
                <strong>{{ connection.shopName }}</strong> trên
                <strong>{{ getPlatformName(connection.platform) }}</strong
                >?
              </p>
              <p class="text-muted mb-0">
                <small
                  >Hành động này sẽ xóa kết nối và bạn cần thiết lập lại để tiếp
                  tục sử dụng.</small
                >
              </p>
            </b-modal>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Loader from '@/view/content/Loader.vue';
import { getToastInstance } from '@/utils/toastHelper';
import { mapGetters } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import StoreAssociationModal from './components/StoreAssociationModal.vue';
import InstallmentAccountModal from './components/InstallmentAccountModal.vue';

const platformRepository = RepositoryFactory.get('platformIntegration');
const storeRepository = RepositoryFactory.get('stores');

export default {
  name: 'PlatformDetailPage',
  components: {
    KTCodePreview,
    Loader,
    StoreAssociationModal,
    InstallmentAccountModal,
  },
  data() {
    return {
      connection: null,
      statistics: null,
      loading: false,
      error: null,
      isRefreshing: false,
      showDisconnectModal: false,
      showStoreAssociationModal: false,
      showInstallmentAccountModal: false,
      availableStores: [],
    };
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    /** Hiển thị TK trả góp theo API: có object, chỉ id (hết hợp lệ), hoặc chưa cấu hình */
    installmentDisplay() {
      const c = this.connection;
      if (!c) {
        return { mode: 'empty' };
      }
      const acc = c.installmentAccount;
      if (acc) {
        const name = (acc.name || '').trim();
        const code = acc.code ? ` (${acc.code})` : '';
        if (name || acc.code) {
          return { mode: 'ok', label: `${name}${code}`.trim() };
        }
        if (acc.id != null) {
          return { mode: 'ok', label: `ID ${acc.id}` };
        }
      }
      if (c.installmentAccountId != null) {
        return { mode: 'invalid', staleId: c.installmentAccountId };
      }
      return { mode: 'empty' };
    },
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
  },
  async created() {
    await this.loadStores();
    await this.loadConnectionDetail();
    this.loadBreadcrumb();
  },
  methods: {
    ...getToastInstance(),

    loadBreadcrumb() {
      const shopName = this.connection?.shopName || 'Chi tiết';
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán', route: 'platform-integration-list' },
        { title: shopName },
      ]);
    },

    async loadStores() {
      try {
        const response = await storeRepository.getByUser();
        this.availableStores = response.stores || [];
      } catch (error) {
        console.error('Error loading stores:', error);
      }
    },

    async loadConnectionDetail() {
      const id = this.$route.params.id;
      if (!id) {
        this.error = 'Không tìm thấy ID kết nối';
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        const response = await platformRepository.getConfigDetail(id);

        this.connection = {
          ...response,
          storeName: response.store?.name || null,
        };

        if (response.stats) {
          this.statistics = {
            productCount: response.stats.productCount || 0,
            pendingOrders: response.stats.pendingOrderCount || 0,
            completedOrders: 0, // Not provided by API yet
          };
        }

        this.loadBreadcrumb();
      } catch (error) {
        console.error('Error loading connection detail:', error);

        if (error.response?.status === 404) {
          this.error = 'Không tìm thấy kết nối này';
        } else if (error.response?.status === 403) {
          this.error = 'Bạn không có quyền truy cập kết nối này';
        } else {
          this.error = 'Không thể tải thông tin kết nối. Vui lòng thử lại.';
        }

        this.makeToastFailure(this.error);
      } finally {
        this.loading = false;
      }
    },

    async refreshConnection() {
      if (!this.connection) return;

      try {
        this.isRefreshing = true;
        await platformRepository.refreshToken(this.connection.id);
        this.$bvToast.toast('Đã làm mới kết nối và cập nhật token thành công', {
          title: 'Thành công',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        });
        await this.loadConnectionDetail();
      } catch (error) {
        console.error('Error refreshing connection:', error);

        if (error.response?.status === 503) {
          this.makeToastFailure(
            `Không thể kết nối với ${this.getPlatformName(
              this.connection.platform,
            )} lúc này. Vui lòng thử lại sau.`,
          );
        } else if (error.response?.status === 401) {
          this.makeToastFailure(
            'Token đã hết hạn hoặc không hợp lệ. Vui lòng kết nối lại.',
          );
        } else {
          this.makeToastFailure('Không thể làm mới kết nối. Vui lòng thử lại.');
        }
      } finally {
        this.isRefreshing = false;
      }
    },

    showChangeStoreModal() {
      this.showStoreAssociationModal = true;
    },

    async handleStoreSaved() {
      this.$bvToast.toast(
        'Đã cập nhật liên kết cửa hàng thành công. Thông tin đã được cập nhật.',
        {
          title: 'Thành công',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        },
      );
      await this.loadConnectionDetail();
    },

    async handleInstallmentAccountSaved() {
      this.$bvToast.toast('Đã cập nhật tài khoản trả góp cho kết nối này.', {
        title: 'Thành công',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000,
      });
      await this.loadConnectionDetail();
    },

    confirmDisconnect() {
      this.showDisconnectModal = true;
    },

    async handleDisconnect() {
      if (!this.connection) return;

      try {
        await platformRepository.disconnect(this.connection.id);
        this.$bvToast.toast(
          `Đã ngắt kết nối với ${this.connection.shopName} thành công`,
          {
            title: 'Thành công',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          },
        );
        this.$router.push({ name: 'platform-integration-list' });
      } catch (error) {
        console.error('Error disconnecting platform:', error);
        this.makeToastFailure('Không thể ngắt kết nối. Vui lòng thử lại.');
      }
    },

    backToList() {
      this.$router.push({ name: 'platform-integration-list' });
    },

    getPlatformLogo(platform) {
      const logos = {
        shopee: '/img/platforms/shopee.svg',
        tiktok: '/img/platforms/tiktok.svg',
      };
      return logos[platform] || '/img/platforms/default.svg';
    },

    getPlatformName(platform) {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[platform] || platform;
    },

    getStatusVariant(status) {
      return status === 'connected' ? 'success' : 'danger';
    },

    getStatusIcon(status) {
      return status === 'connected' ? '🟢' : '🔴';
    },

    getStatusText(status) {
      const statusTexts = {
        connected: 'Đã kết nối',
        error: 'Lỗi',
      };
      return statusTexts[status] || status;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatNumber(value) {
      if (value === null || value === undefined) {
        return '0';
      }
      return value.toLocaleString('vi-VN');
    },

    isTokenExpiringSoon(expiresAt) {
      if (!expiresAt) return false;
      const expiry = new Date(expiresAt);
      const now = new Date();
      const hoursUntilExpiry = (expiry - now) / (1000 * 60 * 60);
      // Show warning if token expires in less than 1 hour
      return hoursUntilExpiry < 1 && hoursUntilExpiry > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-detail-page {
  &__header {
    .platform-logo {
      width: 64px;
      height: 64px;
      object-fit: contain;
    }

    h4 {
      font-weight: 600;
      color: #3f4254;
    }
  }

  &__content {
    .info-item {
      label {
        font-size: 0.875rem;
        display: block;
      }

      strong {
        color: #3f4254;
      }
    }

    .stat-item {
      display: flex;
      align-items: center;
      padding: 1rem;
      background: #f9fafb;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: #f3f6f9;
        transform: translateY(-2px);
      }

      &__icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;

        i {
          font-size: 1.75rem;
        }
      }

      &__content {
        flex: 1;
      }

      &__value {
        font-size: 1.5rem;
        font-weight: 600;
        color: #3f4254;
        line-height: 1.2;
      }

      &__label {
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }
    }
  }

  &__actions {
    .action-buttons {
      display: flex;
      flex-direction: column;
    }
  }

  .notes-list {
    font-size: 0.875rem;
    color: #6c757d;
    padding-left: 1.25rem;
    margin-bottom: 0;

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

:deep(.alert-warning) {
  background-color: #fff8dd;
  border-color: #ffeeba;
  color: #856404;

  .alert-heading {
    color: #856404;
    font-size: 1rem;
    margin-bottom: 0.5rem;

    i {
      margin-right: 0.5rem;
    }
  }
}
</style>
